import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// ─── Axios instance ────────────────────────────────────────────────────────────
export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Attach token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh on 401
api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refresh = localStorage.getItem('refresh_token');
      if (refresh) {
        try {
          const { data } = await axios.post(
            `${API_BASE}/accounts/refresh/`,
            { refresh }
          );
          localStorage.setItem('access_token', data.access);
          original.headers.Authorization = `Bearer ${data.access}`;
          return api(original);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(err);
  }
);

// ─── Context ───────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session on mount — NO useNavigate here
  useEffect(() => {
    const token  = localStorage.getItem('access_token');
    const stored = localStorage.getItem('lpms_user');
    if (token && stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  // Login — returns the user object so the caller can navigate
  const login = useCallback(async (email, password) => {
    const response = await api.post('/accounts/login/', { email, password });
    const payload  = response.data;

    let access, refresh, userData;

    if (payload?.data?.tokens) {
      // Shape A: { data: { tokens: { access, refresh }, user } }
      access   = payload.data.tokens.access;
      refresh  = payload.data.tokens.refresh;
      userData = payload.data.user;
    } else if (payload?.data?.access) {
      // Shape B: { data: { access, refresh, user } }
      access   = payload.data.access;
      refresh  = payload.data.refresh;
      userData = payload.data.user;
    } else {
      // Shape C: flat { access, refresh, user }
      access   = payload.access;
      refresh  = payload.refresh;
      userData = payload.user;
    }

    if (!access) throw new Error('Login failed: no token received.');

    localStorage.setItem('access_token',  access);
    localStorage.setItem('refresh_token', refresh);
    localStorage.setItem('lpms_user',     JSON.stringify(userData));
    setUser(userData);

    // Return userData so Login.jsx can navigate based on role
    return userData;
  }, []);

  // Logout — client-side token wipe. JWTs expire naturally via TTL; no server-side blacklist in v1.0.
  const logout = useCallback(async () => {
    localStorage.clear();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res     = await api.get('/accounts/me/');
      const updated = res.data?.data ?? res.data;
      localStorage.setItem('lpms_user', JSON.stringify(updated));
      setUser(updated);
    } catch {
      // token interceptor handles real 401s
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
